/**
 * Profile Type Definitions
 *
 * Types for customer profile data.
 * Profile contains both encrypted personal data and non-encrypted preferences.
 */

/**
 * Industry verticals supported by the customer portal. Drives copy and feature
 * labels so the same UI can serve customers across industries. Extend this
 * tuple to add a new vertical; the `Industry` union below is derived from it,
 * so `parseIndustry` can never drift from the type.
 *
 * Locale files mirror these keys under `industries.<industry>.*`.
 */
export const INDUSTRY_VALUES = [
  'beauty',
  'wellness',
  'essential_oils',
  'home_goods',
  'food',
  'fashion',
  'jewelry',
  'lifestyle',
] as const

export type Industry = typeof INDUSTRY_VALUES[number]

/** Default industry used when the consultant has not specified one. */
export const DEFAULT_INDUSTRY: Industry = 'beauty'

/** Narrow an untrusted string to a valid Industry, or return undefined. */
export function parseIndustry(value: string | undefined | null): Industry | undefined {
  return value && (INDUSTRY_VALUES as readonly string[]).includes(value)
    ? (value as Industry)
    : undefined
}

/**
 * Communication preference options
 */
export type CommunicationMethod = 'email' | 'phone' | 'sms' | 'whatsapp'

/**
 * Skin type categories (includes consultant's values)
 */
export type SkinType =
  | 'normal'
  | 'dry'
  | 'oily'
  | 'combination'
  | 'sensitive'
  | 'very_dry'
  | 'aging'

/**
 * Skin blemishes frequency (consultant's values)
 */
export type SkinBlemishes = 'never_rarely' | 'sometimes' | 'often'

/**
 * Improvement goals from consultant
 */
export type ImprovementGoal =
  | 'even_tone'
  | 'minimize_wrinkles'
  | 'eye_care'
  | 'extra_moisture'
  | 'body_care'
  | 'lip_care'
  | 'hand_care'

/**
 * Topics of interest from consultant
 */
export type TopicOfInterest =
  | 'skincare_products'
  | 'makeup_tips'
  | 'hostess_gifts'
  | 'mens_skincare'
  | 'fragrances_body'
  | 'business_opportunity'
  | 'gift_ideas'

/**
 * Contact time preferences (includes consultant's values)
 */
export type ContactTime =
  | 'morning'
  | 'afternoon'
  | 'evening'
  | 'anytime'
  | 'weekday_morning'
  | 'weekday_afternoon'
  | 'weekday_evening'
  | 'weekend_morning'
  | 'weekend_afternoon'
  | 'weekend_evening'

/**
 * Skin concern categories
 */
export type SkinConcern =
  | 'aging'
  | 'acne'
  | 'dryness'
  | 'oiliness'
  | 'sensitivity'
  | 'uneven_tone'
  | 'dark_spots'
  | 'redness'
  | 'fine_lines'
  | 'pores'

/**
 * Product category preferences
 */
export type ProductCategory =
  | 'skincare'
  | 'makeup'
  | 'fragrance'
  | 'body_care'
  | 'mens'
  | 'gifts'

/**
 * Contact information
 */
export interface ContactInfo {
  email: string
  phone?: string
  alternatePhone?: string
  address?: {
    street: string
    city: string
    state: string
    postalCode: string
    country: string
  }
}

/**
 * Communication preferences (non-encrypted, customer can modify)
 */
export interface CommunicationPreferences {
  preferredMethod: CommunicationMethod
  preferredMethods?: CommunicationMethod[]
  allowPromotionalEmails: boolean
  allowSmsNotifications: boolean
  allowAppointmentReminders: boolean
  preferredLanguage: string
  preferredContactTime?: ContactTime
}

/**
 * Beauty profile (encrypted, read-only for customer)
 */
export interface BeautyProfile {
  skinType?: SkinType | SkinType[]
  blemishes?: SkinBlemishes[]
  skinConcerns?: SkinConcern[]
  allergies?: string[]
  favoriteProducts?: string[]
  productPreferences?: ProductCategory[]
  notes?: string
  improvementGoals?: ImprovementGoal[]
  sensitivityLevel?: number
  currentRoutine?: string[]
  topicsOfInterest?: TopicOfInterest[]
}

/**
 * Full customer profile (decrypted)
 */
export interface CustomerProfile {
  id: string
  customerId: string
  consultantId: string
  consultantName: string
  consultantWebsite?: string
  /**
   * Industry vertical of the consultant. Used to resolve industry-specific
   * copy via i18n. Falls back to {@link DEFAULT_INDUSTRY} when absent.
   */
  consultantIndustry?: Industry

  // Personal info (encrypted)
  firstName: string
  lastName: string
  displayName?: string
  dateOfBirth?: string
  anniversary?: string

  // Contact (encrypted)
  contact: ContactInfo

  // Beauty profile (encrypted, read-only)
  beautyProfile?: BeautyProfile

  // Preferences (non-encrypted, customer can modify)
  preferences: CommunicationPreferences

  // Metadata
  createdAt: string
  updatedAt: string
  lastOrderDate?: string
  totalOrders?: number
}

/**
 * Request to update customer preferences
 */
export interface UpdatePreferencesRequest {
  preferredMethod?: CommunicationMethod
  allowPromotionalEmails?: boolean
  allowSmsNotifications?: boolean
  allowAppointmentReminders?: boolean
  preferredLanguage?: string
  preferredContactTime?: ContactTime
}

/**
 * Profile summary for dashboard
 */
export interface ProfileSummary {
  displayName: string
  email: string
  consultantName: string
  consultantWebsite?: string
  memberSince: string
  totalOrders: number
}
